import type { SyncPushResult } from '@core/domain/sync/SyncPushResult.ts';
import { AppConfig } from '@core/domain/AppConfig.ts';
import { createCorrelationId } from '@core/domain/utils/correlationId.ts';
import { emitAuthSessionExpired } from '@core/domain/sessionEvents.ts';
import {
  parseSyncPushEnvelope,
  resolveSyncPushResult,
  type SyncPushResponsePayload,
} from '@src/modules/platform/sync/domain/parseSyncPushResponse.ts';
import { wrapSyncApiEnvelope } from '@src/modules/platform/sync/domain/syncApiEnvelope.ts';
import { canGzipHttpBody, encodeGzipHttpBody } from '@src/modules/platform/sync/infrastructure/gzipHttpBody.ts';

type PushResponse = SyncPushResponsePayload;

const PUSH_IDEMPOTENCY_STORAGE_KEY = 'esan-sync-push-idempotency';

/** Replace *Base64 strings with length so fingerprint skips multi-MB JSON (Safari OOM). */
function fingerprintSyncPushValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(fingerprintSyncPushValue);
  if (!value || typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    // ponytail: length proxy; full content hash if same-size media edits collide
    if (/Base64$/i.test(key) && typeof nested === 'string') {
      out[key] = nested.length;
      continue;
    }
    out[key] = fingerprintSyncPushValue(nested);
  }
  return out;
}

function hashSyncPushPayload(body: Record<string, unknown>): string {
  const canonical = JSON.stringify(wrapSyncApiEnvelope(fingerprintSyncPushValue(body) as Record<string, unknown>));
  let h = 5381;
  for (let i = 0; i < canonical.length; i++) {
    h = ((h << 5) + h) ^ canonical.charCodeAt(i);
  }
  return (h >>> 0).toString(16);
}

function canonicalSyncPushBody(body: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const section of ['entregas', 'reposiciones'] as const) {
    const rows = body[section];
    if (!Array.isArray(rows) || rows.length === 0) continue;
    out[section] = [...rows].sort((left, right) => {
      const leftId = left && typeof left === 'object' ? String((left as { publicId?: unknown }).publicId ?? '') : '';
      const rightId = right && typeof right === 'object' ? String((right as { publicId?: unknown }).publicId ?? '') : '';
      return leftId.localeCompare(rightId);
    });
  }
  return out;
}

/** Stable fingerprint for the pending batch (sorted `publicId`s + canonical payload hash). */
export function buildSyncPushBatchFingerprint(body: Record<string, unknown>): string {
  const canonical = canonicalSyncPushBody(body);
  const ids: string[] = [];
  for (const section of ['entregas', 'reposiciones'] as const) {
    const rows = canonical[section];
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      const publicId = (row as { publicId?: unknown }).publicId;
      if (typeof publicId === 'string' && publicId.trim()) ids.push(publicId.trim());
    }
  }
  ids.sort();
  if (ids.length === 0) return '';
  return `${ids.join(',')}|${hashSyncPushPayload(canonical)}`;
}

/** @deprecated Use {@link buildSyncPushBatchFingerprint}. */
export const buildSyncPushIdempotencyKey = buildSyncPushBatchFingerprint;

export function resolveSyncPushIdempotencyKey(body: Record<string, unknown>): string {
  const fingerprint = buildSyncPushBatchFingerprint(body);
  if (!fingerprint) return createCorrelationId();
  try {
    const raw = globalThis.localStorage?.getItem(PUSH_IDEMPOTENCY_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { fingerprint?: string; key?: string };
      if (parsed.fingerprint === fingerprint && typeof parsed.key === 'string' && parsed.key.trim()) {
        return parsed.key.trim();
      }
    }
  } catch {
    /* corrupt storage → new key */
  }
  const key = createCorrelationId();
  try {
    globalThis.localStorage?.setItem(PUSH_IDEMPOTENCY_STORAGE_KEY, JSON.stringify({ fingerprint, key }));
  } catch {
    /* private mode / quota */
  }
  return key;
}

export function clearSyncPushIdempotencyKey(): void {
  try {
    globalThis.localStorage?.removeItem(PUSH_IDEMPOTENCY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

async function readPushErrorBody(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      const error = (body as { error?: unknown }).error;
      if (typeof error === 'string' && error.trim()) return error.trim();
    }
  } catch {
    /* ignore */
  }
  return '';
}

function throwSyncPushHttpError(status: number, errorBody: string): never {
  if (status === 401) {
    emitAuthSessionExpired();
    throw new Error('Sesión expirada. Vuelva a iniciar sesión.');
  }
  if (status === 409) {
    clearSyncPushIdempotencyKey();
    throw new Error('Conflicto de idempotencia: el envío cambió. Intente sincronizar de nuevo.');
  }
  if (status === 400) {
    if (errorBody === 'missing idempotency key') {
      throw new Error('Falta clave de idempotencia en el envío.');
    }
    if (errorBody === 'invalid json') {
      throw new Error('Datos de envío inválidos.');
    }
    throw new Error('No se pudieron enviar registros (código 400).');
  }
  throw new Error(`No se pudieron enviar registros (código ${status}).`);
}

/** Local session tokens (`local.{id}`) are not remote credentials; fall back to the service BEARER_TOKEN. */
export function preferServiceToken(getToken: () => string | null): () => string | null {
  return () => {
    const token = getToken();
    if (token && !token.startsWith('local.')) return token;
    return AppConfig.getCatalogPullToken() || null;
  };
}

export async function postSyncPush(
  body: Record<string, unknown>,
  getToken: () => string | null,
  pushUrl: string,
  opts?: { onUploadProgress?: (ratio: number) => void },
): Promise<SyncPushResult> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return await postSyncPushPreferGzip(body, getToken, pushUrl, opts);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      if (attempt === 0 && /idempotencia/i.test(message)) continue;
      throw e instanceof Error ? e : new Error(message);
    }
  }
  throw new Error('Conflicto de idempotencia: el envío cambió. Intente sincronizar de nuevo.');
}

/** Gzip first; one plain retry if remote treats gzip as invalid JSON. */
async function postSyncPushPreferGzip(
  body: Record<string, unknown>,
  getToken: () => string | null,
  pushUrl: string,
  opts?: { onUploadProgress?: (ratio: number) => void },
): Promise<SyncPushResult> {
  try {
    return await postSyncPushOnce(body, getToken, pushUrl, opts, true);
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    // ponytail: Inclusion may lack request-gzip; drop after backend ships gunzip
    if (!canGzipHttpBody() || !/Datos de envío inválidos/i.test(message)) throw e;
    return await postSyncPushOnce(body, getToken, pushUrl, opts, false);
  }
}

async function postSyncPushOnce(
  body: Record<string, unknown>,
  getToken: () => string | null,
  pushUrl: string,
  opts?: { onUploadProgress?: (ratio: number) => void },
  useGzip = true,
): Promise<SyncPushResult> {
  const payloadBody = canonicalSyncPushBody(body);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Idempotency-Key': resolveSyncPushIdempotencyKey(payloadBody),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const payload = JSON.stringify(wrapSyncApiEnvelope(payloadBody));
  const encoded = useGzip ? await encodeGzipHttpBody(payload) : { body: payload as BodyInit };
  if ('contentEncoding' in encoded && encoded.contentEncoding) {
    headers['Content-Encoding'] = encoded.contentEncoding;
  }

  if (opts?.onUploadProgress && typeof XMLHttpRequest !== 'undefined') {
    return postSyncPushViaXhr(body, encoded.body, headers, pushUrl, opts.onUploadProgress);
  }

  const response = await fetch(pushUrl, {
    method: 'POST',
    headers,
    cache: 'no-store',
    body: encoded.body,
  });
  return parsePostSyncPushResponse(response, body);
}

function postSyncPushViaXhr(
  requestBody: Record<string, unknown>,
  payload: BodyInit,
  headers: Record<string, string>,
  pushUrl: string,
  onUploadProgress: (ratio: number) => void,
): Promise<SyncPushResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', pushUrl);
    xhr.responseType = 'json';
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onUploadProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      void (async () => {
        try {
          if (xhr.status < 200 || xhr.status >= 300) {
            const errorBody = await readXhrPushErrorBody(xhr);
            throwSyncPushHttpError(xhr.status, errorBody);
          }
          clearSyncPushIdempotencyKey();
          const raw = xhr.response ?? JSON.parse(xhr.responseText || 'null');
          const unwrapped = parseSyncPushEnvelope(raw);
          resolve(resolveSyncPushResult(unwrapped, requestBody));
        } catch (e) {
          reject(e instanceof Error ? e : new Error(String(e)));
        }
      })();
    };
    xhr.onerror = () => reject(new Error('No se pudieron enviar registros: error de red.'));
    xhr.send(payload);
  });
}

async function readXhrPushErrorBody(xhr: XMLHttpRequest): Promise<string> {
  try {
    const body = xhr.response ?? JSON.parse(xhr.responseText || 'null');
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      const error = (body as { error?: unknown }).error;
      if (typeof error === 'string' && error.trim()) return error.trim();
    }
  } catch {
    /* ignore */
  }
  return '';
}

async function parsePostSyncPushResponse(
  response: Response,
  requestBody: Record<string, unknown>,
): Promise<SyncPushResult> {
  if (!response.ok) {
    const errorBody = await readPushErrorBody(response);
    throwSyncPushHttpError(response.status, errorBody);
  }
  clearSyncPushIdempotencyKey();
  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new Error('No se pudieron enviar registros: respuesta inválida del servidor.');
  }
  let payload: PushResponse;
  try {
    payload = parseSyncPushEnvelope(raw);
  } catch (e) {
    throw e instanceof Error ? e : new Error('No se pudieron enviar registros: respuesta inválida del servidor.');
  }
  return resolveSyncPushResult(payload, requestBody);
}

export function createSyncPushAdapter(
  sectionKey: 'entregas' | 'reposiciones',
  getToken: () => string | null,
  pushUrl: string = AppConfig.getCatalogPushUrl(),
) {
  return {
    async push<T>(rows: T[]): Promise<SyncPushResult> {
      if (rows.length === 0) return { acceptedPublicIds: [], rejectedPublicIds: [], storedMedia: [] };
      return postSyncPush({ [sectionKey]: rows }, getToken, pushUrl);
    },
  };
}

export function createCombinedSyncPushAdapter(
  getToken: () => string | null,
  pushUrl: string = AppConfig.getCatalogPushUrl(),
) {
  return {
    async push(
      body: Record<string, unknown>,
      opts?: { onUploadProgress?: (ratio: number) => void },
    ): Promise<SyncPushResult> {
      const payload: Record<string, unknown> = {};
      if (Array.isArray(body.entregas) && body.entregas.length > 0) payload.entregas = body.entregas;
      if (Array.isArray(body.reposiciones) && body.reposiciones.length > 0) payload.reposiciones = body.reposiciones;
      if (Object.keys(payload).length === 0) {
        return { acceptedPublicIds: [], rejectedPublicIds: [], storedMedia: [] };
      }
      return postSyncPush(payload, getToken, pushUrl, opts);
    },
  };
}
